import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Scissors, Merge, Archive, Lock, Image, Type, FileText, Maximize2, Zap, MousePointerClick, Search, Eraser, Code, KeyRound, QrCode, Images, PanelsTopLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Footer from '../components/Footer';

const ToolsOverviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const target = document.getElementById(location.hash.slice(1));
    if (target) {
      requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [location.hash]);

  const toolSections = [
    {
      id: 'pdf-tools',
      title: 'PDF Tools',
      description: 'Complete PDF processing suite',
      gradient: 'bg-gradient-to-br from-orange-400 via-red-400 to-pink-500',
      tools: [
        {
          title: 'Split PDF',
          description: 'Extract pages or split by ranges',
          icon: Scissors,
          path: '/split-pdf',
          gradient: 'bg-gradient-to-br from-red-400 to-pink-500',
          usage: '2.4k uses'
        },
        {
          title: 'Merge PDF', 
          description: 'Combine multiple PDFs into one',
          icon: Merge,
          path: '/merge-pdf',
          gradient: 'bg-gradient-to-br from-blue-400 to-indigo-500',
          usage: '1.8k uses'
        },
        {
          title: 'Compress PDF',
          description: 'Reduce PDF file size',
          icon: Archive,
          path: '/compress-pdf',
          gradient: 'bg-gradient-to-br from-green-400 to-emerald-500',
          usage: '1.2k uses'
        },
        {
          title: 'Lock/Unlock PDF',
          description: 'Add or remove password protection',
          icon: Lock,
          path: '/lock-unlock-pdf',
          gradient: 'bg-gradient-to-br from-yellow-400 to-orange-500',
          usage: '890 uses'
        }
      ]
    },
    {
      id: 'image-tools',
      title: 'Image Tools',
      description: 'Convert, resize, and optimize images',
      gradient: 'bg-gradient-to-br from-purple-400 via-pink-400 to-indigo-500',
      tools: [
        {
          title: 'Convert Image',
          description: 'Convert between different formats',
          icon: Image,
          path: '/convert-image',
          gradient: 'bg-gradient-to-br from-purple-400 to-pink-500',
          usage: '3.1k uses'
        },
        {
          title: 'Resize Image',
          description: 'Change image dimensions',
          icon: Maximize2,
          path: '/resize-image',
          gradient: 'bg-gradient-to-br from-indigo-400 to-purple-500',
          usage: '2.7k uses'
        },
        {
          title: 'Compress Image',
          description: 'Reduce image file size',
          icon: Archive,
          path: '/compress-image',
          gradient: 'bg-gradient-to-br from-blue-400 to-cyan-500',
          usage: '1.9k uses'
        },
        {
          title: 'Remove Background',
          description: 'AI-powered background removal',
          icon: Eraser,
          path: '/remove-background',
          gradient: 'bg-gradient-to-br from-pink-400 to-purple-500',
          usage: '1.4k uses'
        }
      ]
    },
    {
      id: 'text-tools',
      title: 'Text Tools',
      description: 'Utilities for text processing',
      gradient: 'bg-gradient-to-br from-green-400 via-emerald-400 to-teal-500',
      tools: [
        {
          title: 'Case Converter',
          description: 'Change text case formats',
          icon: Type,
          path: '/text-case-converter',
          gradient: 'bg-gradient-to-br from-green-400 to-emerald-500',
          usage: '1.5k uses'
        },
        {
          title: 'Find and Replace',
          description: 'Find and replace text instantly',
          icon: Search,
          path: '/text-find-replace',
          gradient: 'bg-gradient-to-br from-emerald-400 to-green-500',
          usage: '980 uses'
        },
        {
          title: 'Online Notepad',
          description: 'Free online text editor with auto-save',
          icon: FileText,
          path: '/online-notepad',
          gradient: 'bg-gradient-to-br from-teal-400 to-cyan-500',
          usage: '2.2k uses'
        }
      ]
    },
    {
      id: 'json-tools',
      title: 'JSON Tools',
      description: 'Format and visualize JSON data',
      gradient: 'bg-gradient-to-br from-cyan-400 via-blue-400 to-indigo-500',
      tools: [
        {
          title: 'JSON Beautifier',
          description: 'Format JSON with expand/collapse',
          icon: Code,
          path: '/json-beautifier',
          gradient: 'bg-gradient-to-br from-cyan-400 to-blue-500',
          usage: '1.1k uses'
        }
      ]
    },
    {
      id: 'utility-tools',
      title: 'Utility Tools',
      description: 'Helpful tools for everyday tasks',
      gradient: 'bg-gradient-to-br from-violet-400 via-purple-400 to-fuchsia-500',
      tools: [
        {
          title: 'QR Code Generator',
          description: 'Create downloadable QR codes instantly',
          icon: QrCode,
          path: '/qr-code-generator',
          gradient: 'bg-gradient-to-br from-cyan-500 to-blue-600',
          usage: 'Private & browser-based'
        },
        {
          title: 'Password Generator',
          description: 'Create strong, secure passwords instantly',
          icon: KeyRound,
          path: '/password-generator',
          gradient: 'bg-gradient-to-br from-violet-500 to-fuchsia-500',
          usage: 'Secure & private'
        }
      ]
    },
    {
      id: 'publishing-tools',
      title: 'Publishing Tools',
      description: 'Prepare polished assets for app stores',
      gradient: 'bg-gradient-to-br from-sky-500 via-indigo-500 to-violet-600',
      tools: [
        {
          title: 'App Screenshot Generator',
          description: 'Create Android and iOS phone and tablet screenshots',
          icon: Images,
          path: '/app-screenshot-generator',
          gradient: 'bg-gradient-to-br from-sky-500 to-indigo-600',
          usage: 'Private & browser-based'
        },
        {
          title: 'Feature Graphic Generator',
          description: 'Create promotional graphics for your app listing',
          icon: PanelsTopLeft,
          path: '/feature-graphic-generator',
          gradient: 'bg-gradient-to-br from-slate-400 to-slate-500',
          usage: 'Google Play ready'
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>AllSmallTooLS — Free Online PDF, Image, Text & Utility Tools</title>
        <meta name="description" content="AllSmallTooLS is a free online toolbox for everyday tasks. Work with PDFs, images, text, JSON, passwords, and more." />
        <meta name="keywords" content="online tools, pdf tools, image tools, text converter, json formatter, password generator, productivity tools" />
        <link rel="canonical" href="https://allsmalltools.com/" />
      </Helmet>

      <div className="space-y-6 pb-12">
      {/* Hero Section */}
      <div className="text-center space-y-4 py-8 sm:py-12 px-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-3xl text-white">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold drop-shadow-lg">
          AllSmallTooLS
        </h1>
        <p className="text-lg sm:text-xl lg:text-2xl max-w-3xl mx-auto font-light px-4">
          Your all-in-one toolkit for PDF, Image, Text, JSON, and everyday utilities
        </p>
        <p className="text-base sm:text-lg opacity-90">Fast • Secure • Free</p>
      </div>

      {/* Tool Sections */}
      {toolSections.map((section) => (
        <div key={section.id} id={section.id} className="space-y-6 scroll-mt-24">
          {/* Section Container - same color background for all tools */}
          <div className={`${section.gradient} rounded-2xl p-8 shadow-xl`}>
            {/* Section Header */}
            <div className="text-white mb-6">
              <h2 className="text-3xl font-bold mb-2">{section.title}</h2>
              <p className="text-lg opacity-90 mb-4">{section.description}</p>
              <p className="text-sm font-semibold">Explore {section.title}</p>
            </div>

            {/* Tools Grid - 4 per row, taller cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {section.tools.map((tool) => {
                const IconComponent = tool.icon;
                // Extract gradient colors for hover effect
                const getGradientClass = (gradient: string) => {
                  if (gradient.includes('red')) return 'group-hover:text-red-500';
                  if (gradient.includes('blue')) return 'group-hover:text-blue-500';
                  if (gradient.includes('green')) return 'group-hover:text-green-500';
                  if (gradient.includes('yellow') || gradient.includes('orange')) return 'group-hover:text-orange-500';
                  if (gradient.includes('purple')) return 'group-hover:text-purple-500';
                  if (gradient.includes('indigo')) return 'group-hover:text-indigo-500';
                  if (gradient.includes('teal')) return 'group-hover:text-teal-500';
                  return 'group-hover:text-indigo-500';
                };
                
                const card = (
                    <Card className="hover:shadow-2xl hover:scale-105 transition-all duration-300 group border-2 border-white/40 shadow-lg overflow-hidden h-full bg-white/95 backdrop-blur-sm">
                      <CardContent className="p-6 h-full flex flex-col min-h-[280px]">
                        {/* Icon */}
                        <div className="flex items-center justify-center mb-6">
                          <div className={`${tool.gradient} p-4 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                            <IconComponent className="h-12 w-12 text-white" />
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 flex flex-col space-y-4 text-center">
                          <div className="space-y-2">
                            <h3 className={`font-bold text-xl text-gray-900 ${getGradientClass(tool.gradient)} transition-colors duration-300`}>
                              {tool.title}
                            </h3>
                            <p className="text-sm text-gray-600 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">{tool.description}</p>
                          </div>
                          <div className="mt-auto pt-4 border-t border-gray-200">
                            <p className={`text-xs font-medium transition-colors duration-300 ${tool.disabled ? 'inline-flex rounded-full bg-slate-200 px-3 py-1 text-slate-700' : 'text-gray-500 group-hover:text-gray-700'}`}>{tool.usage}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                );

                return tool.disabled ? (
                  <div key={tool.title} className="block cursor-not-allowed opacity-75" aria-disabled="true" title="Feature Graphic Generator is coming soon">
                    {card}
                  </div>
                ) : (
                  <Link key={tool.title} to={tool.path} className="block cursor-pointer">
                    {card}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {/* Features Section */}
      <div className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 rounded-3xl p-12 text-white shadow-2xl">
        <div className="text-center space-y-8">
          <h2 className="text-4xl font-bold">Why Choose AllSmallTooLS?</h2>
          <p className="text-sm opacity-90">
            Powered by <a href="https://www.litspark.solutions/?utm_source=allsmalltools" target="_blank" rel="noopener noreferrer" className="font-semibold underline hover:opacity-80 transition-opacity">LitSpark</a>
          </p>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="space-y-4">
              <div className="bg-white/30 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto backdrop-blur-md shadow-lg">
                <Lock className="h-10 w-10" />
              </div>
              <h3 className="font-bold text-2xl">Secure & Private</h3>
              <p className="text-lg opacity-90">Your files are processed securely with no permanent storage</p>
            </div>
            <div className="space-y-4">
              <div className="bg-white/30 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto backdrop-blur-md shadow-lg">
                <Zap className="h-10 w-10" />
              </div>
              <h3 className="font-bold text-2xl">Fast Processing</h3>
              <p className="text-lg opacity-90">Lightning-fast processing with high-quality results</p>
            </div>
            <div className="space-y-4">
              <div className="bg-white/30 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto backdrop-blur-md shadow-lg">
                <MousePointerClick className="h-10 w-10" />
              </div>
              <h3 className="font-bold text-2xl">Easy to Use</h3>
              <p className="text-lg opacity-90">Simple interface with drag-and-drop support</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
          <p className="text-6xl font-bold mb-2">16</p>
          <p className="text-xl">Tools Available</p>
        </div>
        <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl p-8 text-white shadow-xl">
          <p className="text-6xl font-bold mb-2">∞</p>
          <p className="text-xl">Files Processed</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-8 text-white shadow-xl">
          <p className="text-6xl font-bold mb-2">100%</p>
          <p className="text-xl">Free Forever</p>
        </div>
      </div>

      {/* Footer */}
      <Footer />
      </div>
    </>
  );
};

export default ToolsOverviewPage;
